/**
 * Chips describing every active filter, each individually clearable.
 *
 * Not decoration. Most filters live inside a popover, so once it closes they are
 * invisible — and an operator who can't see that a filter is active reads a
 * filtered total as the true total. That is the same misreading as the row
 * truncation this feature fixes, so the chips are worth the vertical space.
 */

import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'

/** A filter reduced to a human label plus how to remove it. */
export interface FilterChip {
  readonly key: string
  readonly label: string
  readonly clear: () => void
}

interface Props {
  readonly chips: readonly FilterChip[]
  readonly onClearAll: () => void
}

export function ActiveFilterChips({ chips, onClearAll }: Props) {
  if (chips.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center gap-1 rounded-full border bg-muted/50 py-1 pl-2.5 pr-1 text-xs"
        >
          <span className="max-w-[260px] truncate">{chip.label}</span>
          <button
            type="button"
            onClick={chip.clear}
            aria-label={`Remove ${chip.label} filter`}
            className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}

      {chips.length > 1 && (
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onClearAll}>
          Clear all
        </Button>
      )}
    </div>
  )
}
